// Cross-page bridge for the engineer's per-branch fulfillment choice.
//
// The cart line stored on the backend doesn't carry a fulfillment method —
// each cart line's method lives only in the Cart page's local state.
// Without persistence, a "delivery" pick made on the product detail page
// would be silently lost the moment the engineer opens their cart, which
// defaults new lines to pickup.
//
// TWO MAPS
//   engineerCartFulfillment      — group-level, `${supplierId}-${branchId}`
//                                  → method. Written by ProductDetail on add,
//                                  used as the FALLBACK for a new cart line
//                                  that has no per-item entry yet.
//   engineerCartItemFulfillment  — line-level, `${cartItemId}` → method.
//                                  Written by Cart page interactions
//                                  (BM-014 Manage Order, BM-015 Change Item).
//                                  Wins over the group-level fallback because
//                                  a single branch can now hold both a
//                                  pickup line and a delivery line and the
//                                  two need to render as separate cards.
//
// Session storage is the right lifetime for both: the cart itself is
// session-scoped and clears with it.

const GROUP_STORAGE_KEY = 'engineerCartFulfillment'
const ITEM_STORAGE_KEY = 'engineerCartItemFulfillment'

export type CartFulfillmentMethod = 'pickup' | 'delivery'

export function fulfillmentKey(
  supplierId: string | number,
  branchId: string | number | null,
): string {
  return `${supplierId}-${branchId}`
}

function readMap(storageKey: string): Record<string, CartFulfillmentMethod> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, CartFulfillmentMethod>
    }
  } catch {
    // Corrupt payload — fall through and return an empty map. A single
    // bad read shouldn't cascade into a broken cart page.
  }
  return {}
}

function writeMap(
  storageKey: string,
  map: Record<string, CartFulfillmentMethod>,
): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(map))
  } catch {
    // Storage full / disabled — nothing to do; the cart page will fall back
    // to its default (pickup unless the branch is delivery-only).
  }
}

// ── Group-level (BC bridge with ProductDetail on-add) ────────────────────

export function readCartFulfillmentMap(): Record<string, CartFulfillmentMethod> {
  return readMap(GROUP_STORAGE_KEY)
}

export function writeCartFulfillmentMethod(
  supplierId: string | number,
  branchId: string | number | null,
  method: CartFulfillmentMethod,
): void {
  const map = readCartFulfillmentMap()
  map[fulfillmentKey(supplierId, branchId)] = method
  writeMap(GROUP_STORAGE_KEY, map)
}

// ── Line-level (BM-015 per-item override, wins over group-level) ─────────

export function readItemFulfillmentMap(): Record<string, CartFulfillmentMethod> {
  return readMap(ITEM_STORAGE_KEY)
}

export function writeItemFulfillmentMethod(
  cartItemId: string,
  method: CartFulfillmentMethod,
): void {
  const map = readItemFulfillmentMap()
  map[cartItemId] = method
  writeMap(ITEM_STORAGE_KEY, map)
}

export function removeItemFulfillmentMethod(cartItemId: string): void {
  const map = readItemFulfillmentMap()
  if (!(cartItemId in map)) return
  delete map[cartItemId]
  writeMap(ITEM_STORAGE_KEY, map)
}
